import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { HttpUtilService } from './http-util.service';
@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private hasAttemptedSourceLanguage;
  private _language;
  private _sourceLanguage = new BehaviorSubject({
    hasErrors: false,
    hasLoaded: false,
    translation: {},
  });
  constructor(
    private http: HttpUtilService,
    private translate: TranslateService
  ) {}
  public translations$ = new BehaviorSubject({});

  get url() {
    return `/api/translations`;
  }

  get browserLanguage() {
    return navigator?.language || window?.navigator?.language || 'en-US';
  }

  get language() {
    return this._language || this.browserLanguage;
  }

  set language(language) {
    this._language = language;
  }

  setSourceLanguage() {
    this.http
      .request('get', `${this.url}?language=en-US`)
      .pipe(
        catchError(error => of({ HAS_ERROR_FROM_INTERNAL_APP_ERROR$$$: error }))
      )
      .subscribe((response: any) => {
        typeof response === 'object' &&
        response !== null &&
        !response.HAS_ERROR_FROM_INTERNAL_APP_ERROR &&
        !response.errorMessage &&
        !Array.isArray(response) &&
        Object.keys(response).length >= 50
          ? this._sourceLanguage.next({
              hasLoaded: true,
              translation: response,
              hasErrors: false,
            })
          : this._sourceLanguage.next({
              hasLoaded: true,
              translation: null,
              hasErrors: true,
            });
      });
  }

  getSourceLanguage$() {
    if (!this.hasAttemptedSourceLanguage) {
      this.setSourceLanguage();
      this.hasAttemptedSourceLanguage = true;
    }
    return this._sourceLanguage.asObservable();
  }

  getTranslatedContent(key = '', defaultText = '') {
    if (!key) return defaultText;
    const translation = this.translate.instant(key) || '';
    // if translation matches key, assume translation wasn't found and return static label, if not found, return key of translation
    return translation !== key ? translation : defaultText || key;
  }

  // used in module for initial default language
  getTranslation() {
    return this.translations$.asObservable();
  }

  getTranslationKey(key) {
    return this.translate.instant(key);
  }

  loadTranslations(language: string) {
    return this.http
      .request('get', `${this.url}?language=${language}`)
      .pipe(retry(3))
      .pipe(
        catchError(error => of({ HAS_ERROR_FROM_INTERNAL_APP_ERROR$$$: error }))
      )
      .pipe(
        map((translations: any) => {
          if (
            !translations ||
            translations?.HAS_ERROR_FROM_INTERNAL_APP_ERROR$$$ ||
            typeof translations !== 'object' ||
            Array.isArray(translations) ||
            Object.keys(translations).length <= 1
          ) {
            return false;
          }
          this.translate.setTranslation(language, translations);
          this.translate.use(language);
          this.language = language;
          return translations;
        })
      );
  }

  handleKeyResponse(response, key, text) {
    if (response?.error || response?.errorMessage) return;
    const currentValue = this._sourceLanguage.getValue();
    currentValue.translation[key] = text;
    this._sourceLanguage.next(currentValue);
  }

  updateKey$(key, text) {
    return this.http
      .request('put', `${this.url}?language=${this.language}`, { key, text })
      .pipe(catchError(error => of({ error })))
      .pipe(tap(response => this.handleKeyResponse(response, key, text)));
  }

  addKey$(key, text) {
    return this.http
      .request('post', `${this.url}?language=${this.language}`, { key, text })
      .pipe(catchError(error => of({ error })))
      .pipe(tap(response => this.handleKeyResponse(response, key, text)));
  }
  capitalizeEachWord(str) {
    const words = str.split(' ');
    const capitalizedWords = words.map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    );
    return capitalizedWords.join(' ');
  }

  transform(text, type): any {
    const content = !text ? '' : this.translate.instant(text);
    return !content || typeof content !== 'string'
      ? ''
      : type === 'uppercase'
        ? content.toUpperCase()
        : type === 'lowercase'
          ? content.toLowerCase()
          : type === 'capitalize'
            ? this.capitalizeEachWord(content)
            : content;
  }
}
